import { format } from "date-fns";
import type { ChipTone } from "@/components/badge/chip";
import { refreshInbox } from "@/hooks/inbox";
import { mutateRequest } from "@/lib/swr";

/** Compact date for RC rows/bars: 25/08/03 (yy/MM/dd). */
export const fmtRcDate = (d?: string | null): string =>
  d ? format(new Date(d), "yy/MM/dd") : "—";

/** Mirror of server RcStatus (server/src/remoteConfigs/rc.entity.ts). */
export const RC_STATUSES = {
  NOT_STARTED: { label: "未开始", tone: "neutral" },
  EXPERIMENTING: { label: "实验中", tone: "green" },
  DEPLOYED: { label: "已部署", tone: "blue" },
  CLOSED: { label: "已回滚", tone: "neutral" },
  ARCHIVED: { label: "存档", tone: "cyan" },
} as const satisfies Record<string, { label: string; tone: ChipTone }>;

type RcStatus = keyof typeof RC_STATUSES;

/** Mirror of server RcType. DEPLOY_CONFIG 显示「参数」(用户 2026-08-28 定名)。 */
export const RC_TYPES = {
  AB_EXPERIMENT: { label: "A/B实验", tone: "purple" },
  DEPLOY_CONFIG: { label: "参数", tone: "cyan" },
  OPS_ADJUST: { label: "运营调整", tone: "orange" },
  TIMELY_OPS_ADJUST: { label: "时效运营", tone: "yellow" },
} as const satisfies Record<string, { label: string; tone: ChipTone }>;

type RcType = keyof typeof RC_TYPES;

/** Conclusion values are stored as the original Feishu select labels. */
export const RC_CONCLUSIONS = ["正向", "负向", "持平"] as const;

/** Badge tone for a stored conclusion (the wire value IS the Chinese literal). */
export const rcConclusionTone = (c?: string | null) =>
  c === "正向" ? "green" : c === "持平" ? "neutral" : "rose";

/** Country options observed in the migrated data (free set, not enforced). */
export const RC_COUNTRIES = [
  "ALL",
  "AR",
  "BR",
  "CO",
  "DE",
  "ES",
  "HK",
  "ID",
  "IT",
  "JP",
  "KR",
  "MX",
  "SA",
  "TH",
  "TW",
  "US",
  "ZA",
  "非BR",
];

export interface RcOperator {
  userId?: string;
  openId?: string;
  name: string;
}

export interface RcAttachment {
  name: string;
  url?: string | null;
  fileToken?: string | null;
}

export interface RcRow {
  id: string;
  appId: string;
  name: string;
  status: RcStatus;
  /** Server-derived review-flow position (list/detail endpoints). */
  stage?: RcStage;
  type: RcType;
  modules?: string[] | null;
  countries?: string[] | null;
  startDate?: string | null;
  endDate?: string | null;
  requesterId?: string | null;
  requester?: { id: string; name?: string; avatarThumb?: string } | null;
  operators?: RcOperator[] | null;
  doc?: string | null;
  docLink?: string | null;
  /** In-app config doc (markdown), written by the wed-rc skill. */
  configDoc?: string | null;
  rcFirebaseLink?: string | null;
  relatedTaskOrPrdLink?: string | null;
  conclusion?: string | null;
  remarks?: string | null;
  deployNote?: string | null;
  /** 等待数据回收节点的 report 回填:文档链接 + 查数终版 ARPU 链接。 */
  reportDocLink?: string | null;
  arpuLink?: string | null;
  arpuScreenshots?: RcAttachment[] | null;
  taskLink?: string | null;
  abReviewTaskLink?: string | null;
  abCloseTaskLink?: string | null;
  messageSent?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/** Flow-node tasks on an RC (backend `RcTask` entity): implement after
 * create, deploy / close after the requester's application clears its
 * review. Any listed assignee can confirm. */
export const RC_TASK_TYPES = {
  approve: { label: "审批", short: "审批", tone: "purple" },
  implement: { label: "配置", short: "配置", tone: "blue" },
  "deploy-approve": { label: "部署审批", short: "部署审批", tone: "purple" },
  deploy: { label: "部署", short: "部署", tone: "green" },
  "close-approve": { label: "回滚审批", short: "回滚审批", tone: "purple" },
  close: { label: "回滚", short: "回滚", tone: "orange" },
} as const satisfies Record<
  string,
  { label: string; short: string; tone: ChipTone }
>;

export interface RcTaskAssignee {
  userId?: string | null;
  openId?: string | null;
  name: string;
}

export interface RcTask {
  id: string;
  rcId: string;
  type: keyof typeof RC_TASK_TYPES;
  assignees?: RcTaskAssignee[] | null;
  dueAt: string;
  confirmedAt?: string | null;
  confirmedBy?: string | null;
  /** approve tasks only: set = the approval was rejected (rc closed). */
  rejectedAt?: string | null;
  rejectReason?: string | null;
  remindCount?: number;
  createdAt?: string;
  /** Loaded by the pending endpoint. */
  rc?: RcRow | null;
}

/** bitable 迁移来源行(id=飞书 record_id);站内新建为 uuid。 */
export const isLegacyRc = (id: string) => id.startsWith("rec");

/** Unified row badge: flow position, same vocabulary as the list tabs
 * (待审批/待配置/实验中/已部署/存档/已回滚)。历史迁移行的 CLOSED 显示
 * 历史词「已关闭」——旧数据没区分存档/回滚,不声称配置是否已下线;
 * 站内流程走出来的 CLOSED 才是「已回滚」。 */
export const rcStageBadge = (
  rc: Pick<RcRow, "id" | "status" | "stage">,
): { label: string; tone: ChipTone } => {
  if (rc.stage === "approve") return { label: "待审批", tone: "purple" };
  if (rc.stage === "implement") return { label: "待配置", tone: "orange" };
  if (rc.status === "CLOSED" && isLegacyRc(rc.id)) {
    return { label: "已关闭", tone: "neutral" };
  }
  return RC_STATUSES[rc.status] ?? { label: rc.status, tone: "neutral" };
};

export const isRcTaskAssignee = (task: RcTask, userId?: string): boolean =>
  !!userId && (task.assignees ?? []).some((a) => a.userId === userId);

/** Revalidate every RC-task list: my inbox (+ nav badge) and the per-RC list. */
export const refreshRcTasks = (rcId?: string) => {
  refreshInbox();
  if (rcId) mutateRequest(`/remote-configs/${rcId}/tasks`);
};

/** Review-flow stages the list tabs filter by (server `stage` param). */
export type RcStage =
  | "approve"
  | "implement"
  | "experimenting"
  | "deployed"
  | "archived"
  | "close"
  | "conclusion"
  | "closed";

export interface RcStats {
  total: number;
  byStatus: Partial<Record<RcStatus, number>>;
  byType: Partial<Record<RcType, number>>;
  overdue: number;
  conclusionTodo: number;
  /** Per review-flow stage counts (tab badges). */
  stages?: Partial<Record<RcStage, number>>;
}
